import PropTypes from 'prop-types';
import React from 'react';
import DialogPortal from './DialogPortal';
import JitsiPortal from './JitsiPortal';
import Drawer from '@mui/material/Drawer';
import withBoundary from 'component/ErrorBoundary/withBoundary';
import { isMobileBrowser } from 'store/modules/base/environment/utils';
import { connect } from 'store/modules/base/redux';
import { getContextMenuStyle } from 'store/modules/overlay';

/**
 * Implements a React {@code Component} for showing an {@code Popover} on
 * mouseenter of the trigger and contents, and hiding the dialog on mouseleave.
 *
 * @augments Component
 */
class Popover extends React.PureComponent {
  /**
   * Default values for {@code Popover} component's properties.
   *
   * @static
   */
  static defaultProps = {
    className: '',
    id: '',
    trigger: 'hover'
  };

  /**
   * Initializes a new {@code Popover} instance.
   *
   * @param {Object} props - The read-only properties with which the new
   * instance is to be initialized.
   */
  constructor(props) {
    super(props);

    this.state = {
      contextMenuStyle: null
    };

    // Bind event handlers so they are only bound once for every instance.
    this._onHideDialog = this._onHideDialog.bind(this);
    this._onShowDialog = this._onShowDialog.bind(this);
    this._onKeyPress = this._onKeyPress.bind(this);
    this._containerRef = React.createRef();
    this._onEscKey = this._onEscKey.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._setContextMenuRef = this._setContextMenuRef.bind(this);
    this._setContextMenuStyle = this._setContextMenuStyle.bind(this);
    this._getCustomDialogStyle = this._getCustomDialogStyle.bind(this);
    this._onOutsideClick = this._onOutsideClick.bind(this);
  }

  /**
   * Sets up a touch event listener to attach.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentDidMount() {
    window.addEventListener('touchstart', this._onTouchStart);
    if (this.props.trigger === 'click') {
      // @ts-ignore
      window.addEventListener('click', this._onOutsideClick);
    }
  }

  /**
   * Removes the listener set up in the {@code componentDidMount} method.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentWillUnmount() {
    window.removeEventListener('touchstart', this._onTouchStart);
    if (this.props.trigger === 'click') {
      window.removeEventListener('click', this._onOutsideClick);
    }
  }

  /**
   * Handles click outside the popover.
   *
   * @param {MouseEvent} e - The click event.
   * @returns {void}
   */
  _onOutsideClick(e) {
    if (
      !this._containerRef?.current?.contains(e.target) &&
      this.props.visible
    ) {
      this._onHideDialog();
    }
  }

  /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
  render() {
    const {
      children,
      className,
      content,
      id,
      overflowDrawer,
      visible,
      trigger
    } = this.props;

    if (overflowDrawer) {
      return (
        <div className={className} id={id} onClick={this._onShowDialog}>
          {children}
          <JitsiPortal>
            <Drawer isOpen={visible} onClose={this._onHideDialog}>
              {content}
            </Drawer>
          </JitsiPortal>
        </div>
      );
    }

    return (
      <div
        className={className}
        id={id}
        onClick={this._onClick}
        onKeyPress={this._onKeyPress}
        {...(trigger === 'hover'
          ? {
              onMouseEnter: this._onShowDialog,
              onMouseLeave: this._onHideDialog
            }
          : {})}
        ref={this._containerRef}>
        {visible && (
          <DialogPortal
            getRef={this._setContextMenuRef}
            setSize={this._setContextMenuStyle}
            style={this.state.contextMenuStyle}>
            {this._renderContent()}
          </DialogPortal>
        )}
        {children}
      </div>
    );
  }

  /**
   * Sets the context menu dialog style for positioning it on screen.
   *
   * @param {DOMRectReadOnly} size -The size info of the current context menu.
   *
   * @returns {void}
   */
  _setContextMenuStyle(size) {
    const style = this._getCustomDialogStyle(size);

    this.setState({ contextMenuStyle: style });
  }

  /**
   * Sets the context menu's ref.
   *
   * @param {HTMLElement} elem -The html element of the context menu.
   *
   * @returns {void}
   */
  _setContextMenuRef(elem) {
    this._contextMenuRef = elem;
  }

  /**
   * Hide dialog on touch outside of the context menu.
   *
   * @param {TouchEvent} event - The touch event.
   * @private
   * @returns {void}
   */
  _onTouchStart(event) {
    if (
      this.props.visible &&
      !this.props.overflowDrawer &&
      this._contextMenuRef &&
      this._contextMenuRef.contains && // @ts-ignore
      !this._contextMenuRef.contains(event.target)
    ) {
      this._onHideDialog();
    }
  }

  /**
   * Stops displaying the {@code Popover}.
   *
   * @private
   * @returns {void}
   */
  _onHideDialog() {
    this.setState({
      contextMenuStyle: null
    });

    if (this.props.onPopoverClose) {
      this.props.onPopoverClose();
    }
  }

  /**
   * Displays the {@code Popover} and calls any registered onPopoverOpen
   * callbacks.
   *
   * @param {Object} event - The mouse event or the keypress event to intercept.
   * @private
   * @returns {void}
   */
  _onShowDialog(event) {
    event?.stopPropagation();

    if (!this.props.disablePopover) {
      this.props.onPopoverOpen?.();
    }
  }

  /**
   * Prevents switching from tile view to stage view on accidentally clicking
   * the popover thumbs.
   *
   * @param {Object} event - The mouse event or the keypress event to intercept.
   * @private
   * @returns {void}
   */
  _onClick(event) {
    const { trigger, visible } = this.props;

    event.stopPropagation();
    if (trigger === 'click') {
      if (visible) {
        this._onHideDialog();
      } else {
        this._onShowDialog();
      }
    }
  }

  /**
   * KeyPress handler for accessibility.
   *
   * @param {Object} e - The key event to handle.
   *
   * @returns {void}
   */
  _onKeyPress(e) {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      if (this.props.visible) {
        this._onHideDialog();
      } else {
        this._onShowDialog(e);
      }
    }
  }

  /**
   * KeyPress handler for accessibility.
   *
   * @param {Object} e - The key event to handle.
   *
   * @returns {void}
   */
  _onEscKey(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (this.props.visible) {
        this._onHideDialog();
      }
    }
  }

  /**
   * Gets style for positioning the context menu on screen in regards to the trigger's
   * position.
   *
   * @param {DOMRectReadOnly} size -The current context menu's size info.
   *
   * @returns {Object} - The new style of the context menu.
   */
  _getCustomDialogStyle(size) {
    if (this._containerRef?.current) {
      const bounds = this._containerRef.current.getBoundingClientRect();

      return getContextMenuStyle(bounds, size, this.props.position);
    }
  }

  /**
   * Renders the React Element to be displayed in the {@code Popover}.
   * Also adds padding to support moving the mouse from the trigger to the
   * dialog to prevent mouseleave events.
   *
   * @private
   * @returns {ReactElement}
   */
  _renderContent() {
    const { content } = this.props;

    return (
      <div className="popover" onKeyDown={this._onEscKey}>
        {content}
        {!isMobileBrowser() && (
          <>
            <div className="popover-mousemove-padding-top" />
            <div className="popover-mousemove-padding-right" />
            <div className="popover-mousemove-padding-left" />
            <div className="popover-mousemove-padding-bottom" />
          </>
        )}
      </div>
    );
  }
}

/**
 * Maps (parts of) the Redux state to the associated {@code Popover}'s props.
 *
 * @param {Object} state - The Redux state.
 * @param {Object} ownProps - The own props of the component.
 * @private
 * @returns {IProps}
 */
function _mapStateToProps(state) {
  return {
    overflowDrawer: state['features/toolbox'].overflowDrawer
  };
}

/**
 * The type of the React {@code Component} props of {@link Popover}.
 */
Popover.propTypes = {
  /**
   * A child React Element to use as the trigger for showing the dialog.
   */
  children: PropTypes.any,

  /**
   * Additional CSS classnames to apply to the root of the {@code Popover}
   * component.
   */
  className: PropTypes.string,

  /**
   * The ReactElement to display within the dialog.
   */
  content: PropTypes.any,

  /**
   * Whether displaying of the popover should be prevented.
   */
  disablePopover: PropTypes.bool,

  /**
   * An id attribute to apply to the root of the {@code Popover}
   * component.
   */
  id: PropTypes.string,

  /**
   * Callback to invoke when the popover has closed.
   */
  onPopoverClose: PropTypes.func,

  /**
   * Callback to invoke when the popover has opened.
   */
  onPopoverOpen: PropTypes.func,

  /**
   * Whether to display the Popover as a drawer.
   */
  overflowDrawer: PropTypes.bool,

  /**
   * Where should the popover content be placed.
   */
  position: PropTypes.string,

  /**
   * Whether the trigger for open/ close should be click or hover.
   */
  trigger: PropTypes.string,

  /**
   * Whether the popover is visible or not.
   */
  visible: PropTypes.bool
};

export default React.memo(connect(_mapStateToProps)(withBoundary(Popover)));
